/**
 * Integration Card Component
 * 
 * Displays individual integration information with status, actions, and health metrics
 */

import React, { useState } from 'react'
import { 
  Settings, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Zap,
  MoreVertical,
  Trash2,
  Eye,
  Pause,
  
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Progress } from '@/components/ui/progress'
import { useIntegration } from '@/features/integrations/hooks/useIntegrations'
import { integrationUtils } from '@/features/integrations/utils/integrationUtils'
import { IntegrationConfig } from '@/types/integrations'

interface IntegrationCardProps {
  integration: IntegrationConfig
  onRefresh?: () => void
  variant?: 'default' | 'detailed'
}

export function IntegrationCard({ 
  integration, 
  onRefresh: _onRefresh,
  variant = 'default' 
}: IntegrationCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const { 
    testConnection, 
    syncIntegration, 
    isTesting, 
    isSyncing 
  } = useIntegration(integration.id)

  const statusConfig = {
    connected: {
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
    },
    disconnected: {
      icon: AlertTriangle,
      color: 'text-muted-foreground',
      bgColor: 'bg-muted',
      borderColor: 'border-border',
    },
    error: {
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
    },
    syncing: {
      icon: RefreshCw,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
    },
    connecting: {
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
    },
    suspended: {
      icon: Pause,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
    },
  }

  const config = statusConfig[integration.status] || statusConfig.disconnected
  const StatusIcon = config.icon

  const healthScore = integrationUtils.getHealthScore(
    integration.error_count,
    integration.last_sync,
    integration.status === 'connected' ? 95 : 0
  )

  const handleTestConnection = () => {
    testConnection()
  }

  const handleSync = () => {
    syncIntegration()
  }

  const getNextSyncTime = () => {
    if (!integration.last_sync || !integration.sync_frequency) return null
    return integrationUtils.getNextSyncTime(integration.last_sync, integration.sync_frequency)
  }

  const nextSync = getNextSyncTime()

  return (
    <Card className={`${config.borderColor} ${config.bgColor} transition-all duration-200 hover:shadow-md`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="text-2xl">
              {integrationUtils.typeIcons[integration.type]}
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">
                {integration.name}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {integrationUtils.typeLabels[integration.type]}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge 
              variant={integration.status === 'connected' ? 'default' : 'secondary'}
              className={`${config.color} border-0`}
            >
              <StatusIcon className="w-3 h-3 mr-1" />
              {integrationUtils.statusLabels[integration.status]}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setIsExpanded(!isExpanded)}>
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleTestConnection}
                  disabled={isTesting}
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Test Connection
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleSync}
                  disabled={isSyncing || integration.status !== 'connected'}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sync Now
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Health Score */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Health Score</span>
            <span className={`font-medium ${
              healthScore >= 80 ? 'text-green-600' : 
              healthScore >= 60 ? 'text-yellow-600' : 
              'text-red-600'
            }`}>
              {healthScore}%
            </span>
          </div>
          <Progress 
            value={healthScore} 
            className="h-2"
          />
        </div>

        {/* Status Information */}
        <div className="space-y-3 text-sm">
          {integration.last_sync && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Last Sync</span>
              <span>{integrationUtils.formatRelativeTime(integration.last_sync)}</span>
            </div>
          )}
          
          {nextSync && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Next Sync</span>
              <span>{integrationUtils.formatRelativeTime(nextSync)}</span>
            </div>
          )}
          
          {integration.sync_frequency && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Frequency</span>
              <span>{integrationUtils.syncFrequencyLabels[integration.sync_frequency]}</span>
            </div>
          )}
          
          {integration.error_count > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Errors</span>
              <span className="text-red-600 font-medium">{integration.error_count}</span>
            </div>
          )}
        </div>

        {/* Error Message */}
        {integration.last_error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700 font-medium">Latest Error</p>
            <p className="text-xs text-red-600 mt-1">{integration.last_error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 mt-4">
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleTestConnection}
            disabled={isTesting}
            className="flex-1"
          >
            {isTesting ? (
              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Zap className="w-3 h-3 mr-1" />
            )}
            Test
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleSync}
            disabled={isSyncing || integration.status !== 'connected'}
            className="flex-1"
          >
            {isSyncing ? (
              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3 mr-1" />
            )}
            Sync
          </Button>
        </div>

        {/* Expanded Details */}
        {(isExpanded || variant === 'detailed') && (
          <div className="mt-4 pt-4 border-t border-border">
            <h4 className="font-medium text-sm mb-3">Features</h4>
            <div className="grid grid-cols-1 gap-2">
              {integrationUtils.getFeatures(integration.type).map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-xs">
                  <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                  <span className="text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
